import {
  ColorModeProvider,
  Dialog,
  DialogContent,
  DialogHeader,
  ProgressBar,
  Tooltip,
} from '@airbus/components-react';
import RestoreIcon from '@mui/icons-material/Restore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  DataGridPremium,
  GridActionsCellItem,
  GridCellModes,
  GridCellModesModel,
  GridCellParams,
  gridClasses,
  GridColDef,
  GridFilterModel,
  GridLogicOperator,
  GridSortModel,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { SplitPane, Pane } from 'react-split-pane';
import { t } from '../../../i18next';
import { useAlertBar } from '../../context/AlertBarContext';
import { useColumnContextAPI } from '../../context/GetAllColumnContext';
import GridDataContextProvider, { useGridDataContext } from '../../context/GridDataContext';
import { useLanguageContextAPI } from '../../context/LanguageContext';
import { useUserContextApi } from '../../context/UserContext';
import { fetchGridRows } from '../../services/gridRowsService';
import {
  customValidator,
  filterUpsertedRows,
  hasDisabledSelectedOptionWarningForGrid,
  isRowMatchingQuickFilter,
  usePinnedActionColumn,
} from '../../utils';
import { handleClipboardCopy } from '../../utils/clipboardUtils';
import { prepareGridData } from '../../utils/dataTransformers';
import { transformFilterItems } from '../../utils/transformFilterItems';
import useGridColumnAccess from '../../utils/useGridColumnAccess';
import { withErrorBoundary } from '../layout/ErrorBoundary';
import RightPanel from '../RightPanelForm';
import Spinners from '../Spinner';
import { buildSearchQuery } from './buildSearchQuery';
import CustomGridFilterPanel from './CustomGridFilterPanel';
import { DeletionRequestAction } from './DeletionRequestAction';
import { EditToolbar } from './GridHeader';
import { getApplyQuickFilterFnExceptStatusCols, getRowClassName } from './GridHelper';
import { PaginationComponent } from './PaginationComponent';
import { RowErrorAction } from './RowErrorAction';
import SetColumnConfig, { getHeaderName } from './SetColumnConfig';
import { withAirbusTooltip } from './withAirbusTooltip';
import { getApiBaseUrl } from '../../utils/apiBase';
import useBreakpoint from '../useBreakpoint';
import { MOBILE_MEDIA_QUERY } from '../breakpoints';
import {
  NOTIFICATION_EVENTS,
  subscribeNotificationEvent,
} from '../../services/notification/workflow';

function debounce(func, timeout = 500) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func(...args);
    }, timeout);
  };
}

export default withErrorBoundary(ServerSideGridWithContext);

export const defaultSorting = {
  rowGrouping: {
    model: [],
  },
  pinnedColumns: {},
  columns: {
    orderedFields: [],
  },
  preferencePanel: {
    open: false,
  },
  filter: {
    filterModel: {
      items: [],
      logicOperator: GridLogicOperator.And,
      quickFilterValues: [],
      quickFilterLogicOperator: GridLogicOperator.And,
    },
  },
  sorting: {
    sortModel: [],
  },
  pagination: { paginationModel: { pageSize: 1000, page: 0 } },
};

function ServerSideGridWithContext(props) {
  return (
    <GridDataContextProvider>
      <ServerSideGrid {...props} />
    </GridDataContextProvider>
  );
}

const getPaneSize = (isOpen, hasAccess, size) => (isOpen && hasAccess ? size : '0px');

function ServerSideGrid(props) {
  const { gridBoxHeight } = props;
  const classes = useStyles();
  const { tableRes } = props;
  const { mbdDataGrid } = tableRes;
  const { userDetails } = useUserContextApi();
  const { language } = useLanguageContextAPI();
  const { roleIds, screenID } = userDetails;

  const previousSelectedViewIdRef = useRef<string | number | null>(null);

  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useGridDataContext();
  const [rowModesModel, setRowModesModel] = useState({});
  const [cellModesModel, setCellModesModel] = useState<GridCellModesModel>({});
  const [rowClicked, setRowClicked] = useState(null);
  const [isFormPanelOpen, setIsFormPanelOpen] = useState(!mbdDataGrid?.panelCancelBtn);
  const [isRowLoading, setIsRowLoading] = useState(true);
  const [actionProp] = useState(null);
  const [accordionDetails, setAccordionDetails] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 1000,
  });
  const [rowCount, setRowCount] = useState(0);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [filterItems, setFilterItems] = useState<GridFilterModel>({
    items: [],
    logicOperator: GridLogicOperator.And,
  });
  const { setAlertbar, messages } = useAlertBar();
  const [quickFilterValue, setQuickFilterValue] = useState('');
  const searchTriggerRef = useRef(false);
  const [columnTypes, setColumnTypes] = useState(null);
  const [expandGrid, setExpandGrid] = useState(false);
  const apiRef = useGridApiRef();
  const serverSideQuickSearch = useFeatureFlagEnabled('serverSideQuickSearch');
  const [unfilteredRows, setUnfilteredRows] = useState({ rows: [], count: 0 });
  const previousFilterItemsRef = useRef<string>('');
  const [rightPaneSize, setRightPaneSize] = useState(510);
  const [isGridReady, setIsGridReady] = useState(false);

  const {
    reRender,
    updatereRender,
    allColumns,
    allAccessibleColumns,
    viewData,
    selectedViewName,
    setStateAllAccessibleColumnData,
    setSelectedViewName,
    selectedView,
    setSelectedView,
    setInitialViewColumns,
    allDropdowns,
    setStateAllRowsData,
    accessDetails: access,
    tableFeatureData,
    customConfig,
    updateRightTabMenuDetails,
    isDirectConfigScreen,
  } = useColumnContextAPI();
  const ColumnConfig = customConfig?.SetColumnConfig ?? SetColumnConfig;
  const AdditionalFilterQuery = customConfig?.AdditionalFilterQuery ?? '';
  const AdditionalSortQuery = customConfig?.AdditionalSortQuery ?? '';
  const ScreenSlotProps = customConfig?.ScreenSlotProps ?? {};
  const apiBase = getApiBaseUrl();
  const { isColumnConfigLoading, isError } = useGridColumnAccess({
    tableRes,
    roleIds,
    screenID,
    useColumnContextAPI,
  });

  const isMobile = useBreakpoint(MOBILE_MEDIA_QUERY);

  if (isError) {
    setRows(() => []);
    setColumns(() => []);
    setAccordionDetails(null);
    setAlertbar({
      children: t('data_not_available'),
      severity: 'warning',
    });
  }

  useEffect(() => {
    setStateAllRowsData(rows);
  }, [rows]);

  useEffect(() => {
    const removeGridRefreshListener = subscribeNotificationEvent(
      NOTIFICATION_EVENTS.DATA_REFRESH,
      () => {
        updatereRender();
      },
    );

    return () => {
      removeGridRefreshListener();
    };
  }, [updatereRender]);

  useEffect(() => {
    if (customConfig?.ScreenSlotProps?.customColumns) {
      setStateAllAccessibleColumnData(() => customConfig?.ScreenSlotProps?.customColumns);
      setLocalColumns(customConfig?.ScreenSlotProps?.customColumns);
    }
    if (customConfig?.ScreenSlotProps?.customRows) {
      setRows(customConfig?.ScreenSlotProps?.customRows ?? null);
      setStateAllRowsData(customConfig?.ScreenSlotProps?.customRows ?? null);
      setIsFormPanelOpen(false);
      setIsRowLoading(false);
      setAlertbar([]);
    }
    if (customConfig?.ScreenSlotProps?.customSorting) {
      defaultSorting.sorting = customConfig?.ScreenSlotProps?.customSorting;
    }
  }, [customConfig]);

  useEffect(() => {
    if (allColumns && access && allDropdowns && !isDirectConfigScreen) {
      const { gridColumns } = ColumnConfig({
        allColumns,
        access,
        allDropdowns,
        language,
        viewData,
        customProps: {
          updateRow,
          setAlertbar,
          tableFeatureData,
          language,
          ...ScreenSlotProps,
          screenGridId: tableRes.id,
        },
      });

      setStateAllAccessibleColumnData(gridColumns);
    }
  }, [allColumns, access, allDropdowns, language]);

  useEffect(() => {
    if (!allAccessibleColumns) return;
    if (!hasMountedRef.current) return;

    if (searchTriggerRef.current && paginationModel.page === 0) {
      setLocalRows(0, paginationModel.pageSize);
      searchTriggerRef.current = false;
      return;
    }

    if (!searchTriggerRef.current) {
      setLocalRows(paginationModel.page, paginationModel.pageSize);
    }
  }, [
    paginationModel.page,
    paginationModel.pageSize,
    sortModel,
    serverSideQuickSearch && quickFilterValue,
  ]);

  useEffect(() => {
    if (!allAccessibleColumns) return;

    const cleanedFilters = filterItems.items.filter((item) => {
      if (item.operator === 'isEmpty' || item.operator === 'isNotEmpty') {
        return true;
      }

      const value = item?.value;
      if (value === undefined || value === null) {
        return false;
      }

      return String(value).trim() !== '';
    });

    const signature = JSON.stringify({
      logicOperator: filterItems.logicOperator,
      items: cleanedFilters,
    });

    if (previousFilterItemsRef.current !== signature) {
      setLocalRows(paginationModel.page, paginationModel.pageSize);
      previousFilterItemsRef.current = signature;
    }
  }, [filterItems]);

  useEffect(() => {
    if (serverSideQuickSearch) return;
    if (!allAccessibleColumns || quickFilterValue === null || isRowLoading) return;

    const matchingRows = unfilteredRows.rows.filter(
      (row) =>
        isRowMatchingQuickFilter({
          colDef: allAccessibleColumns,
          row,
          quickFilterValues: [quickFilterValue],
        }) && row.status === '',
    );

    const upsertedRows = filterUpsertedRows(rows);

    setRows([...upsertedRows, ...matchingRows]);
  }, [quickFilterValue, isRowLoading]);

  useEffect(() => {
    setIsFormPanelOpen(false);
    setLocalRows(paginationModel.page, paginationModel.pageSize, false);
  }, [reRender]);

  useEffect(() => {
    if (viewData && allAccessibleColumns?.length) {
      const hasStandardView = viewData.find((x) => x.standardView);
      if (!hasStandardView) {
        setAlertbar({
          children: t('standard_view_not_available'),
          severity: 'warning',
        });
        return;
      }
      const defaultView = viewData.find((x) => x.defaultView);
      let selected = null;
      if (selectedViewName) {
        selected = viewData.find((x) => x.viewName === selectedViewName);
      } else if (defaultView) {
        selected = defaultView;
      } else if (hasStandardView) {
        selected = hasStandardView;
      }
      if (!selected) {
        selected = selectedView?.id && viewData.find((x) => x.id === selectedView.id);
      }

      const initialColumns = getOrderedColumns({ selectedView: selected, allAccessibleColumns });
      const columnsList =
        initialColumns
          ?.filter((item) => item.attrAvailableInGrid)
          .map((item) => item.attrInternalName) ?? [];
      setSelectedViewName(selected?.viewName);
      setSelectedView(structuredClone(selected));
      setInitialViewColumns(columnsList);
    }
  }, [selectedViewName, viewData, allAccessibleColumns]);

  useEffect(() => {
    if (selectedView) {
      const viewColumns = getOrderedColumns({ selectedView, allAccessibleColumns });
      setLocalColumns(viewColumns);
    }
  }, [customConfig, selectedView, selectedViewName, language, accordionDetails]);

  useEffect(() => {
    if (!selectedView || (!isGridReady && !Object.keys(apiRef?.current)?.length)) return;
    if (
      previousSelectedViewIdRef.current != null &&
      selectedView?.id === previousSelectedViewIdRef.current
    )
      return;
    else previousSelectedViewIdRef.current = null;
    if (!previousSelectedViewIdRef.current) {
      previousSelectedViewIdRef.current = selectedView?.id ?? null;
      let newState = {
        ...defaultSorting,
        ...selectedView?.gridFilter,
      };
      if (customConfig?.ScreenSlotProps?.customSorting) {
        newState.sorting = customConfig?.ScreenSlotProps?.customSorting;
      }
      if (selectedView.gridFilter == null) {
        newState = {
          pagination: {
            paginationModel: {
              page: 0,
              pageSize: access.noOfRowsPerPage,
            },
          },
          filter: {
            filterModel: {
              items: [],
              logicOperator: GridLogicOperator.And,
              quickFilterValues: [],
            },
          },
          sorting: {
            sortModel: [],
          },
        };
      }
      const { filter, sorting, pagination } = newState;
      setTimeout(() => {
        if (sorting?.sortModel) {
          apiRef?.current?.setSortModel(sorting.sortModel);
        }
        if (pagination?.paginationModel) {
          apiRef?.current?.setPaginationModel(pagination.paginationModel);
        }
        if (filter?.filterModel) {
          apiRef?.current?.setFilterModel(filter.filterModel);
        }
      }, 10);
    }
  }, [selectedView, isGridReady]);

  const memoizedColumns = useMemo(() => {
    return columns.filter((col) => col.attrInternalName !== 'undoDelete');
  }, [columns]);

  const isDeletionRequestedRow = (row) => !!customConfig?.SetIsDeletionRequestedRow?.({ row });

  const hasDisabledSelectedOptionWarning = (params) => {
    return hasDisabledSelectedOptionWarningForGrid({
      field: params.field,
      value: params.value,
      row: params.row,
      colDef: params.colDef,
      allDropdowns,
      customConfig,
    });
  };

  const buildCombinedFilter = () => {
    const trimmedQuickFilter = quickFilterValue?.trim();
    const hasQuickSearch = !!trimmedQuickFilter;

    const searchFilter =
      hasQuickSearch && serverSideQuickSearch
        ? buildSearchQuery(trimmedQuickFilter, memoizedColumns, columnTypes)
        : '';

    const hasSearch = !!searchFilter;
    const hasBase = !!AdditionalFilterQuery?.trim();

    if (hasSearch && hasBase) {
      return `${AdditionalFilterQuery}  ${searchFilter}  `;
    }

    if (hasBase) {
      return AdditionalFilterQuery;
    }

    if (hasSearch) {
      return searchFilter;
    }

    return '';
  };

  const quickSearchAPI = (query: string) => {
    setQuickFilterValue(query);
    searchTriggerRef.current = true;
    setPaginationModel((prev) => ({
      ...prev,
      page: 0,
    }));
  };

  const setLocalRows = async (page: number, pageSize: number, loadUpsertedRows = true) => {
    if (!allAccessibleColumns) {
      return;
    }
    setIsRowLoading(true);
    setAccordionDetails(null);
    const serverFilterModel = transformFilterItems(filterItems, allAccessibleColumns);

    const rawBody = {
      columns: allAccessibleColumns
        .filter((col) => col.attrAvailableInGrid)
        .map((col) => ({
          field: col.attrInternalName,
          type: col.attrDataType,
        })),
      filterModel: serverFilterModel,
      limit: pageSize,
      offset: pageSize * page,
      screenGridId: tableRes.id,
      sortModel,
      customFilter: buildCombinedFilter(),
    };
    const body = JSON.parse(JSON.stringify(rawBody));
    const res = await fetchGridRows(`${apiBase}/api/dataGrid`, body);
    if (res.status && res.status === 401) {
      setIsRowLoading(false);
      setAlertbar({ children: t('error.invalid_session'), severity: 'error', stopAutoClose: true });
      return;
    }
    if (!res || res?.errors) {
      setIsRowLoading(false);
      setAlertbar({ children: t('error.error_fetching'), severity: 'error' });
      return;
    }
    const querySource = mbdDataGrid.dataGridSource;

    setExpandGrid(
      !!quickFilterValue.trim() || filterItems?.items?.some((f) => f?.value?.toString().trim()),
    );
    const tableSTName = `${mbdDataGrid.schemaName}_${querySource}`;
    const { SetRowLogic } = customConfig;
    const rowData = SetRowLogic ? SetRowLogic(res[tableSTName]) : res[tableSTName];
    setColumnTypes(res.columns);

    const rowsWithPreciseNumbers = prepareGridData(rowData, body.columns);

    const dArray = rowsWithPreciseNumbers.map((result, index) => {
      const data = { status: '', backup: { ...result }, ...result };
      data.id = mbdDataGrid.dataGridUniqueIdentifier
        ? result[mbdDataGrid.dataGridUniqueIdentifier]
        : index + 1;
      return data;
    });

    setRows((prestate) => {
      if (!loadUpsertedRows) return [...dArray];
      const upsertedRows = prestate.filter(({ status }) => ['N', 'C', 'E', 'D'].includes(status));
      const upsertedRowIds = upsertedRows.map(({ id }) => id);
      const gridRows = dArray.filter(({ id }) => !upsertedRowIds.includes(id));

      return [...upsertedRows, ...gridRows];
    });

    if (isFormPanelOpen && dArray.length > 0) {
      setRowSelectionModel([dArray[0].id]);
      setRowClicked(dArray[0]);
    } else {
      setRowSelectionModel([]);
      setRowClicked(null);
    }

    setStateAllRowsData([...dArray]);
    const rowCount = res[`${tableSTName}_aggregate`]?.aggregate?.count || 0;
    setRowCount(rowCount);
    setUnfilteredRows({ rows: [...dArray], count: rowCount });
    setIsRowLoading(false);
  };

  const setLocalColumns = (colData) => {
    if (!colData.length) {
      setAlertbar({
        children: t('no_columns_found'),
        severity: 'warning',
      });
    } else if (messages && messages[0]?.children === t('no_columns_found')) {
      setAlertbar([]);
    }
    const defaultColumns = [
      {
        attrInternalName: 'undoDelete',
        field: 'undoDelete',
        headerName: '',
        minWidth: 80,
        width: 80,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        disableReorder: true,
        disableExport: true,
        attrDisplayNameen: '',
        attrDisplayNamede: '',
        getApplyQuickFilterFn: getApplyQuickFilterFnExceptStatusCols,
        renderCell: (params) => {
          const action = [];
          const { status } = params.row;
          const hasAnyError =
            (accordionDetails?.length ?? 0) > 0 || rows?.some((r) => r?.isCommitRow);
          const exists = new Map(accordionDetails?.map((item) => [item.id, item])).has(params?.id);
          const hasDeletionRequest = isDeletionRequestedRow(params.row);

          if (params.row?.isCommitRow) {
            const { isValid, errors } = customValidator({
              allColumnDefs: allAccessibleColumns,
              row: params.row,
              SetISCellRequired: customConfig?.SetISCellRequired,
              language,
            });
            if (!isValid && Object.keys(errors).length) {
              action.push(
                <GridActionsCellItem
                  icon={
                    <WarningAmberIcon
                      sx={{
                        color: '#ff0000 !important',
                      }}
                    />
                  }
                  label='Error'
                />,
              );
            }
          }
          if (exists) {
            const matchedItem = (new Map(accordionDetails.map((item) => [item.id, item])).get(
              params?.id ?? '',
            ) as {
              rowno: string;
              column: Record<string, { field: string; msg: string }>;
            }) ?? { rowno: '', column: {} };

            action.push(
              <RowErrorAction
                params={params}
                matchedItem={matchedItem}
                apiRef={apiRef}
                columns={columns}
              />,
            );
          }

          if (hasDeletionRequest) {
            action.push(<DeletionRequestAction />);
          }

          return (
            <div className={classes.actionRow}>
              <div
                className={classes.errorSlot}
                style={{
                  minWidth: hasAnyError || hasDeletionRequest ? 30 : 0,
                  marginRight: hasAnyError || hasDeletionRequest ? -5 : 0,
                }}
              >
                {action}
              </div>

              <div
                className={classes.undoSlot}
                style={{
                  paddingLeft: hasAnyError ? 5 : 10,
                }}
              >
                {(status === 'D' || status === 'E' || status === 'N' || status === 'C') && (
                  <GridActionsCellItem
                    icon={<RestoreIcon sx={{ color: 'secondary' }} />}
                    label='Restore'
                    onClick={() => gridActionRevertHandler(params)}
                  />
                )}
              </div>
            </div>
          );
        },
      },
    ];
    const gridColumns = colData
      .filter((item) => item.attrAvailableInGrid)
      .map((item) => {
        const originalRenderCell = item.renderCell;

        return {
          ...item,
          minWidth: 50,
          width: item.width || 150,
          renderCell: (params) => {
            const cellContent =
              typeof originalRenderCell === 'function'
                ? originalRenderCell(params)
                : params.formattedValue ?? params.value ?? '';

            if (!hasDisabledSelectedOptionWarning(params)) {
              return cellContent;
            }

            return (
              <Tooltip label={t('invalid_dropdown_value')} placement='top'>
                <div style={{ width: '100%', height: '100%' }}>{cellContent}</div>
              </Tooltip>
            );
          },
        };
      });
    setColumns(withAirbusTooltip([...defaultColumns, ...gridColumns]));
  };

  const hasMountedRef = useRef(false);
  const prevRowIdsRef = useRef(new Set());
  const prevAccordionRef = useRef([]);
  const pendingRowFlagRef = useRef(false);
  const newRowIdRef = useRef(null);

  useEffect(() => {
    if (!Array.isArray(rows)) return;

    const currentIds = new Set(rows.map((r) => r?.id));

    if (!hasMountedRef.current) {
      prevRowIdsRef.current = currentIds;
      hasMountedRef.current = true;
      return;
    }
    const newRow = rows.find((r) => r?.status === 'N' && !prevRowIdsRef.current.has(r?.id));

    if (newRow?.id != null) {
      newRowIdRef.current = newRow.id;
      pendingRowFlagRef.current = true;
    }
    prevRowIdsRef.current = currentIds;
  }, [rows]);

  useEffect(() => {
    if (!Array.isArray(accordionDetails)) return;

    const accordionChanged =
      JSON.stringify(prevAccordionRef.current) !== JSON.stringify(accordionDetails);

    const anyObjectChanged = accordionDetails.some((item, index) => {
      const prevItem = prevAccordionRef.current[index];
      return JSON.stringify(item) !== JSON.stringify(prevItem);
    });

    const isValidAccordion = accordionDetails.length > 0;
    const hasNewRow = pendingRowFlagRef.current && newRowIdRef.current != null;

    const rowInAccordion = hasNewRow
      ? accordionDetails.some((item) => item?.id === newRowIdRef.current)
      : false;

    const shouldTriggerAlert =
      (hasNewRow && accordionChanged && isValidAccordion && rowInAccordion) || anyObjectChanged;

    if (shouldTriggerAlert) {
      setAlertbar({
        title: t('rowErrorValidation.title'),
        children: t('rowErrorValidation.children'),
        severity: 'error',
      });

      if (hasNewRow) {
        pendingRowFlagRef.current = false;
        newRowIdRef.current = null;
      }
    }
    prevAccordionRef.current = accordionDetails;
  }, [accordionDetails]);

  const updateSelectionAfterRowRemoval = (id, newState) => {
    if (!isFormPanelOpen) {
      setRowSelectionModel((prev) => prev?.filter((selectedId) => selectedId !== id));
      return;
    }

    const lastRecord = newState.find((item) => item.id === rowSelectionModel[0]);
    setRowSelectionModel([lastRecord?.id ?? newState[0]?.id]);
    setRowClicked(lastRecord ?? newState[0]);
  };

  const removeRowFromState = (id) => {
    setRows((prestate) => {
      const newState = [...prestate];
      const index = newState.findIndex((item) => item.id == id);
      if (index >= 0) {
        newState.splice(index, 1);
      }

      updateSelectionAfterRowRemoval(id, newState);
      return newState;
    });
  };

  const refreshPinnedColumns = () => {
    setTimeout(() => {
      if (apiRef.current) {
        const currentPinned = apiRef.current.state.pinnedColumns;
        apiRef.current.setPinnedColumns(currentPinned);
        apiRef.current.resize();
      }
    }, 0);
  };

  const removeChangedRow = (id) => {
    setTimeout(() => {
      removeRowFromState(id);
      refreshPinnedColumns();
    }, 200);
  };

  const restoreRowFromBackup = (id, backup) => {
    const initialRow = {
      id: backup[mbdDataGrid.dataGridUniqueIdentifier],
      status: '',
      backup: { ...backup },
      ...backup,
    };

    setTimeout(() => {
      setRowClicked(initialRow);
    }, 500);

    setRows((prestate) => {
      const newState = [...prestate];
      const index = newState.findIndex((item) => item.id == id);
      if (index >= 0) {
        newState.splice(index, 1, initialRow);
      }
      return newState;
    });

    refreshPinnedColumns();
  };

  const gridActionRevertHandler = (params) => {
    const { id, row } = params;
    const { backup, status } = row;

    if (status === 'N' || status === 'C') {
      removeChangedRow(id);
    } else {
      restoreRowFromBackup(id, backup);
    }

    setAccordionDetails((prev) => prev?.filter((detail) => detail?.id !== id));
  };

  const processRowUpdate = (newRow, oldRow) => {
    if (JSON.stringify(oldRow) === JSON.stringify(newRow)) {
      return oldRow;
    } else {
      const updatedRow = {
        ...newRow,
        status: newRow?.status || 'E',
      };
      if (isFormPanelOpen && rowClicked?.id === updatedRow.id) {
        setRowClicked(() => updatedRow);
      }
      return updatedRow;
    }
  };

  const onRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };
  const updateRow = ({ rowId, updatedFields }) => {
    setRows((prestate) => {
      const newState = [...prestate];
      const index = newState.findIndex((item) => item.id == rowId);
      const row = newState.find((item) => item.id === rowId);

      const newRow = { ...row, ...updatedFields };
      newState.splice(index, 1, newRow);
      return newState;
    });
  };

  const columnVisibilityModel = {
    errorIcon: Boolean(accordionDetails),
  };
  if (mbdDataGrid.dataGridUniqueIdentifier && mbdDataGrid.dataGridUniqueIdentifier !== 'id1') {
    columnVisibilityModel[mbdDataGrid.dataGridUniqueIdentifier] = false;
  }
  const slotPropsToolbar = {
    setrows: setRows,
    rows,
    setrowmodesmodel: setRowModesModel,
    rowselectionmodel: rowSelectionModel,
    setRowSelectionModel,
    setrowclicked: setRowClicked,
    isformpanelopen: isFormPanelOpen,
    setAlertbar,
    setAccordionDetails,
    columns,
    selectedViewName,
    setColumns,
    isRowLoading,
    quickSearchAPI: quickSearchAPI,
  };

  const createViewModeRow = (rowModel) => {
    const viewModeRow = {};
    if (!rowModel) {
      return viewModeRow;
    }

    Object.keys(rowModel).forEach((field) => {
      viewModeRow[field] = { mode: GridCellModes.View };
    });

    return viewModeRow;
  };

  const buildCellModesModel = (prevModel, params) => {
    const newModel = {};

    Object.keys(prevModel || {}).forEach((id) => {
      newModel[id] = createViewModeRow(prevModel[id]);
    });

    newModel[params.id] = {
      ...createViewModeRow(prevModel[params.id]),
      [params.field]: { mode: GridCellModes.Edit },
    };

    return newModel;
  };

  const handleCellClick = React.useCallback((params: GridCellParams, event: React.MouseEvent) => {
    if (!params?.isEditable) {
      return;
    }

    if (
      (event.target as any).nodeType === 1 &&
      !event.currentTarget.contains(event.target as Element)
    ) {
      return;
    }

    setCellModesModel((prevModel) => buildCellModesModel(prevModel, params));
  }, []);

  const onCellModesModelChange = useCallback((newModel: GridCellModesModel) => {
    setCellModesModel(newModel);
  }, []);

  const onCellClick = (params, event) => {
    handleCellClick(params, event);
    if (isFormPanelOpen) {
      setRowClicked({ ...params.row });
    }
  };

  const onCellDoubleClick = (params, event) => {
    const colType = params?.colDef?.type || params?.colDef?.attrDataType;
    if (colType === 'date') {
      event.defaultMuiPrevented = true;
      return;
    }
    if (!event.ctrlKey) {
      event.defaultMuiPrevented = true;
    }
    setRowClicked(params?.row);
    setIsFormPanelOpen(true);
    setRightPaneSize(510);
    updateRightTabMenuDetails(0);
  };

  const handleFilterChange = (filterModel: GridFilterModel) => {
    const { items, logicOperator } = filterModel;
    if (JSON.stringify(filterItems) === JSON.stringify(filterModel)) return;
    // Convert isAnyOf string values to numbers for numeric types;
    // otherwise, pass values as-is.
    const normalizedItems = items.map((item) => {
      if (item?.value === undefined || item?.value === null || item?.value === '') {
        return item;
      }
      if (item?.operator !== 'isAnyOf' || !Array.isArray(item?.value)) {
        return item;
      }

      const fieldName = item?.field;
      const columnConfig = allAccessibleColumns?.find((col) => col.attrInternalName === fieldName);

      if (columnConfig?.attrDataType !== 'number') {
        return item;
      }

      const numericValues = item.value
        .map((value) => (value === '' || value == null ? null : Number(value)))
        .filter((value) => value !== null && !Number.isNaN(value));

      return {
        ...item,
        value: numericValues,
      };
    });

    setFilterItems({ items: normalizedItems, logicOperator });

    if (!serverSideQuickSearch) {
      const quickFilterValues = filterModel.quickFilterValues;
      if (quickFilterValues && quickFilterValues.length > 0) {
        if (quickFilterValues[0] !== quickFilterValue) {
          setQuickFilterValue(quickFilterValues[0]);
        }
      } else if (quickFilterValue) {
        setQuickFilterValue('');
      }
    }
  };

  const gridProps = {
    onPaginationModelChange: setPaginationModel,
    paginationMode: 'server',
    onSortModelChange: setSortModel,
    sortingMode: 'server',
    onFilterModelChange: debounce(handleFilterChange),
    filterMode: 'server',
    columnVisibilityModel,
    rowModesModel,
    rowSelectionModel,
    paginationModel,
    getRowClassName: (params) => {
      const baseClassName = getRowClassName(params, accordionDetails);
      return isDeletionRequestedRow(params.row)
        ? `${baseClassName} deletionRequestedRow`.trim()
        : baseClassName;
    },
    onRowModesModelChange,
    processRowUpdate,
    cellModesModel,
    onCellModesModelChange,
    onCellClick,
    onCellDoubleClick,
    pagination: !AdditionalSortQuery,
    sorting: true,
    ignoreDiacritics: true,
    autoHeight: false,
    editMode: 'cell',
    rowHeight: 40,
    loading: isRowLoading,
    disableColumnSelector: true,
    disableRowGrouping: true,
    pageSizeOptions: [1000, 500, 100, 50, 20, 10],
    slots: {
      toolbar: EditToolbar,
      pagination: PaginationComponent,
      loadingOverlay: LinearProgress,
      filterPanel: CustomGridFilterPanel,
    },
    slotProps: {
      toolbar: slotPropsToolbar,
      filterPanel: { columnsSort: 'asc' },
    },
    onStateChange: () => {
      if (!isGridReady) {
        setIsGridReady(true);
      }
    },
    isCellEditable: (params) => {
      if (isDeletionRequestedRow(params.row)) {
        return false;
      }
      if (customConfig?.SetIsCellEditable) {
        const { field, row } = params;
        const { SetIsCellEditable } = customConfig;
        return SetIsCellEditable({
          field,
          row,
          access,
          allAccessibleColumns,
          language,
          allDropdowns,
        });
      }
      return params.row.status !== 'D' && access?.edit_row;
    },
    onProcessRowUpdateError: (error) => {
      return error;
    },
    onRowSelectionModelChange: (rowSelectionModel) => {
      setRowSelectionModel(rowSelectionModel);
      if (rowSelectionModel.length) {
        const [lastElementId] = rowSelectionModel.slice(-1);
        const selectedLastRow = rows.find((item) => item.id === lastElementId);
        setRowClicked(selectedLastRow);
      }
      if (customConfig?.GetSelectedRowData) {
        const selectedRowData = rows.filter((item) => rowSelectionModel.includes(item.id));
        const { GetSelectedRowData } = customConfig;
        GetSelectedRowData(selectedRowData);
      }
    },
    ...customConfig?.ScreenSlotProps?.customGridProps,
    getCellClassName: (params) => {
      const warningClassName = hasDisabledSelectedOptionWarning(params)
        ? 'disabledSelectedOptionCell'
        : '';

      return warningClassName;
    },
  };

  if ((!viewData && !isDirectConfigScreen) || isColumnConfigLoading) {
    return <Spinners />;
  }

  if (!access) {
    return <Spinners />;
  }

  if (columns.length < 1) {
    return <Spinners />;
  }

  return (
    <div className={classes.gridContainer}>
      <div className={classes.gridBox} style={gridBoxHeight ? { height: gridBoxHeight } : {}}>
        <SplitPane
          resizable={true}
          direction='horizontal'
          onResize={(sizes) => {
            setRightPaneSize(sizes[1]);
          }}
          dividerStyle={{
            width: getPaneSize(isFormPanelOpen, access?.singleDatasetGroup, '0.3rem'),
          }}
        >
          <Pane>
            <DataGridContainer
              gridProps={gridProps}
              columns={columns}
              language={language}
              rows={rows}
              actionProp={actionProp}
              rowCount={rowCount}
              selectedViewName={selectedViewName}
              apiRef={apiRef}
              expandGrid={expandGrid}
            />
          </Pane>
          {isMobile && isFormPanelOpen && access?.singleDatasetGroup ? (
            <ColorModeProvider mode='light'>
              <Dialog
                open={isFormPanelOpen && !!rowClicked}
                onClose={() => setIsFormPanelOpen(false)}
                aria-labelledby='right-panel-dialog-title'
                aria-describedby='right-panel-dialog-description'
                style={{ padding: 0, gap: 0 }}
              >
                <DialogHeader style={{ padding: '35px 0 25px' }}></DialogHeader>
                <DialogContent className={classes.rightPanelContentResponsive}>
                  <div style={{ height: 'calc(100vh - 140px)' }}>
                    <RightPanel
                      setisformpanelopen={setIsFormPanelOpen}
                      rowValues={rowClicked}
                      setrows={setRows}
                      rows={rows}
                      setAlertbar={setAlertbar}
                    />
                  </div>
                </DialogContent>
              </Dialog>
            </ColorModeProvider>
          ) : (
            <Pane
              minSize='300px'
              size={getPaneSize(isFormPanelOpen, access?.singleDatasetGroup, rightPaneSize)}
            >
              <div className={classes.paneContent}>
                {!!rows.length && access?.singleDatasetGroup && isFormPanelOpen && (
                  <RightPanel
                    setisformpanelopen={setIsFormPanelOpen}
                    rowValues={rowClicked}
                    setrows={setRows}
                    rows={rows}
                    setAlertbar={setAlertbar}
                  />
                )}
              </div>
            </Pane>
          )}
        </SplitPane>
      </div>
    </div>
  );
}
const getOrderedColumns = (props) => {
  const { selectedView, allAccessibleColumns } = props;
  return selectedView?.viewColumns.reduce((resp, item) => {
    const columnConfig = allAccessibleColumns?.find((res) => res.attrInternalName === item);
    if (columnConfig) {
      resp.push(columnConfig);
    }
    return resp;
  }, []);
};
function LinearProgress() {
  return <ProgressBar indeterminate />;
}

function DataGridContainer(props) {
  const { gridProps, rows, language, columns, rowCount, selectedViewName, apiRef } = props;

  const { selectedView, setSelectedView, viewData, customConfig } = useColumnContextAPI();

  gridProps.slotProps.toolbar.apiRef = apiRef;
  gridProps.onCellEditStart = onCellEditStart;

  const [newColumns, setNewColumns] = useState<GridColDef[]>([]);
  const [newRows, setNewRows] = useState([]);

  const [viewColumnWidths, setViewColumnWidths] = useState<Record<string, Record<string, number>>>(
    {},
  );

  usePinnedActionColumn({
    rows,
    apiRef,
    setNewRows,
    setIsDeletionRequestedRow: customConfig?.SetIsDeletionRequestedRow,
  });

  useEffect(() => {
    if (!columns?.length) return;

    const gridWidth = apiRef?.current?.rootElementRef?.current?.clientWidth || window.innerWidth;

    const preparedColumns: GridColDef[] = columns.map((item) => {
      const { headerName, label } = getHeaderName(item, item.attrMandatory, language);
      const widthInfo = getFieldDimensions(item.field, viewData);

      const cachedWidth = viewColumnWidths[selectedViewName]?.[item.field];

      const baseWidth = cachedWidth ?? widthInfo?.width ?? item.attrMinWidth ?? 150;

      if (item.field === 'undoDelete' || item.field === 'id') {
        const baseWidthForUndoDelete = cachedWidth ?? 80;
        return {
          ...item,
          headerName,
          label,
          minWidth: 80,
          width: baseWidthForUndoDelete,
          flex: 0,
        };
      }

      return {
        ...item,
        headerName,
        label,
        minWidth: 50,
        width: baseWidth,
        flex: 0,
        resizable: true,
      };
    });

    const totalWidth = preparedColumns.reduce((sum, col) => sum + (col.width ?? 150), 0);
    if (totalWidth < gridWidth) {
      preparedColumns.forEach((col) => {
        if (col.field !== 'undoDelete' && col.field !== 'id') {
          col.flex = 1;
        }
      });
    }

    setNewColumns(preparedColumns);
  }, [columns, language, selectedViewName, viewColumnWidths]);

  //useEffect(() => {
  //  if (actionProp) {
  //    const { id, field, rowIndex } = actionProp;
  //    const state = apiRef.current.state;
  //    const pagesize = state.pagination.paginationModel.pageSize;
  //    let rowIndexVal;
  //    state.sorting.sortedRows.forEach((value, index) => {
  //      if (value === id) {
  //        rowIndexVal = index;
  //      }
  //    });
  //    const goto = Math.ceil(
  //      state.sorting.sortModel.length ? rowIndexVal / pagesize : rowIndex / pagesize,
  //    );
  //    apiRef.current.setPage(goto - 1);
  //    apiRef.current.setCellFocus(id, field);
  //    const colindex = apiRef.current.getColumnIndex(field, false);
  //    setTimeout(() => {
  //      const row = document.querySelectorAll(
  //        `.MuiDataGrid-virtualScroller .MuiDataGrid-virtualScrollerRenderZone [data-id='` +
  //          id +
  //          `'] [data-field=` +
  //          field +
  //          `]`,
  //      );
  //      if (row?.length) {
  //        row[0].scrollIntoView(true);
  //        if (apiRef.current) {
  //          apiRef.current.scrollToIndexes({ rowIndex: rowIndex, colIndex: colindex - 1 });
  //        }
  //      }
  //    }, 10);
  //  }
  //}, [actionProp]);

  function getFieldDimensions(field, viewDataArray) {
    if (!field || !Array.isArray(viewDataArray) || !selectedViewName) return null;
    if (field === 'undoDelete' || field === 'id') return null;

    const selectedView = viewDataArray.find((view) => view.viewName === selectedViewName);
    const columns = selectedView?.viewColumns || [];

    for (let i = 0; i < columns.length; i++) {
      if (
        columns[i] === field &&
        typeof columns[i + 1] === 'string' &&
        columns[i + 1].startsWith('width:')
      ) {
        const widthMatch = columns[i + 1].match(/width:(\d+)px/);
        if (widthMatch) {
          const width = Number(widthMatch[1]);

          return {
            width,
          };
        }
      }
    }

    return null;
  }

  function onCellEditStart(params, event) {
    if (event.code != 'Enter') {
      const { id, field, row } = params;
      setTimeout(() => {
        apiRef?.current?.setEditCellValue({ id, field, value: row[field] });
      }, 10);
    }
  }
  const hasPathNames = Array.isArray(rows) && rows.some((r) => Array.isArray(r?.path_names));

  const treeProps = hasPathNames
    ? {
        getRowId: (row: any) => {
          const path = (row.path_names ?? row.path) || row.pathName || '';
          return `${row.id}::${path || 'root'}`;
        },
        treeData: true as const,
        getTreeDataPath: (row: any) => row?.path_names || [],
        defaultGroupingExpansionDepth: props.expandGrid ? -1 : 0,
        groupingColDef: {
          headerName: '',
          hideDescendantCount: false,
        } as const,
      }
    : {};

  return (
    gridProps && (
      <DataGridPremium
        {...gridProps}
        {...treeProps}
        columns={newColumns}
        rows={newRows}
        rowCount={rowCount}
        apiRef={apiRef}
        initialState={{
          pinnedColumns: {
            left: [],
          },
        }}
        disableAggregation={true}
        cellSelection
        columnReordering
        onClipboardCopy={() => handleClipboardCopy(apiRef)}
        onColumnOrderChange={() => {
          if (!apiRef.current || !selectedView) return;

          const orderedFields = apiRef.current
            .getAllColumns()
            .map((col) => col.field)
            .filter((field) => field !== 'undoDelete');

          const oldViewColumns = selectedView.viewColumns || [];

          const newViewColumns: string[] = [];

          orderedFields.forEach((field) => {
            newViewColumns.push(field);

            const index = oldViewColumns.indexOf(field);

            if (
              index !== -1 &&
              typeof oldViewColumns[index + 1] === 'string' &&
              oldViewColumns[index + 1].startsWith('width:')
            ) {
              newViewColumns.push(oldViewColumns[index + 1]);
            }
          });

          setSelectedView((prev) => ({
            ...prev,
            viewColumns: newViewColumns,
          }));
        }}
        onColumnWidthChange={(params) => {
          if (!selectedViewName) return;

          setViewColumnWidths((prev) => {
            const currentViewWidths = prev[selectedViewName] || {};

            return {
              ...prev,
              [selectedViewName]: {
                ...currentViewWidths,
                [params.colDef.field]: params.width,
              },
            };
          });
        }}
        sx={{
          height: '100%',
          minHeight: 0,
          maxHeight: '100%',
          ...(gridProps.isUndoing && {
            '& .MuiDataGrid-row.Mui-selected': {
              backgroundColor: 'transparent !important',
            },
          }),
          '& .MuiDataGrid-row': {
            transition: 'none !important',
          },
          '& .MuiDataGrid-virtualScroller': {
            overflowY: 'auto',
          },
          '& .newRow': {
            backgroundColor: '#7EC0A91A',
          },
          '& .newRow .MuiDataGrid-cell': {
            borderTop: '1px solid #7EC0A9',
          },
          '& .newRow .MuiDataGrid-cell[data-field="undoDelete"]': {
            borderLeft: '3px solid #08875B !important',
          },
          '& .editedRow': {
            backgroundColor: '#879CE91A',
          },
          '& .editedRow .MuiDataGrid-cell': {
            borderTop: '1px solid #879CE9',
          },

          '& .editedRow .MuiDataGrid-cell[data-field="undoDelete"]': {
            borderLeft: '3px solid #255FCC !important',
          },
          '& .deletedRow': {
            backgroundColor: '#FCC1BD1A',
          },
          '& .deletedRow .MuiDataGrid-cell': {
            borderTop: '1px solid #FCC1BD',
          },

          '& .deletedRow .MuiDataGrid-cell[data-field="undoDelete"]': {
            borderLeft: '3px solid #E4002B !important',
          },

          '& .MuiDataGrid-cell[data-field="undoDelete"]': {
            paddingLeft: '0px !important',
            paddingRight: '0px !important',
            zIndex: 1300,
          },
          '& .clonedRow': {
            backgroundColor: '#7EC0A91A',
          },
          '& .clonedRow .MuiDataGrid-cell': {
            borderTop: '1px solid #7EC0A9',
          },
          '& .clonedRow .MuiDataGrid-cell[data-field="undoDelete"]': {
            borderLeft: '3px solid #08875B !important',
          },
          '& .errorRow .MuiDataGrid-cell[data-field="undoDelete"]': {
            borderLeft: '3px solid #E4002B !important',
          },
          '& .MuiDataGrid-row.Mui-selected.newRow': {
            backgroundColor: '#7EC0A91A !important',
          },
          '& .MuiDataGrid-row.Mui-selected.clonedRow': {
            backgroundColor: '#7EC0A91A !important',
          },
          '& .MuiDataGrid-row.Mui-selected.editedRow': {
            backgroundColor: '#879CE91A !important',
          },
          '& .MuiDataGrid-row.Mui-selected.deletedRow': {
            backgroundColor: '#FCC1BD1A !important',
          },
          '& .MuiDataGrid-columnHeader--withRightBorder .MuiDataGrid-columnSeparator': {
            opacity: 1,
          },
          '& .MuiDataGrid-row.newRow:hover': {
            backgroundColor: '#7EC0A91A !important',
          },
          '& .MuiDataGrid-row.editedRow:hover': {
            backgroundColor: '#879CE91A !important',
          },
          '& .MuiDataGrid-row.deletedRow:hover': {
            backgroundColor: '#FCC1BD1A !important',
          },
          '& .MuiDataGrid-row.clonedRow:hover': {
            backgroundColor: '#7EC0A91A !important',
          },
          '& .MuiDataGrid-row.newRow .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-row.newRow .MuiDataGrid-cell--pinnedRight':
            {
              backgroundColor: '#f3f9f7 !important',
              borderTop: '1px solid #7EC0A9',
            },
          '& .MuiDataGrid-row.editedRow .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-row.editedRow .MuiDataGrid-cell--pinnedRight':
            {
              backgroundColor: '#f3f6fd !important',
              borderTop: '1px solid #879CE9',
            },
          '& .MuiDataGrid-row.deletedRow .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-row.deletedRow .MuiDataGrid-cell--pinnedRight':
            {
              backgroundColor: '#fff9f9 !important',
              borderTop: '1px solid #FCC1BD',
            },
          '& .MuiDataGrid-row.clonedRow .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-row.clonedRow .MuiDataGrid-cell--pinnedRight':
            {
              backgroundColor: '#f3f9f7 !important',
              borderTop: '1px solid #7EC0A9',
            },

          '& .MuiDataGrid-row.newRow:hover .MuiDataGrid-cell[data-field="undoDelete"]': {
            backgroundColor: '#f3f9f7 !important',
          },

          '& .MuiDataGrid-row.editedRow:hover .MuiDataGrid-cell[data-field="undoDelete"]': {
            backgroundColor: '#f3f6fd !important',
          },

          '& .MuiDataGrid-row.deletedRow:hover .MuiDataGrid-cell[data-field="undoDelete"]': {
            backgroundColor: '#fff9f9 !important',
          },

          '& .MuiDataGrid-row.clonedRow:hover .MuiDataGrid-cell[data-field="undoDelete"]': {
            backgroundColor: '#f3f9f7 !important',
          },
          '& .deletionRequestedRow': {
            backgroundColor: '#f2f4f7 !important',
          },
          '& .deletionRequestedRow .MuiDataGrid-cell': {
            color: '#8c8f94',
          },
          '& .deletionRequestedRow .MuiDataGrid-cell--pinnedLeft, & .deletionRequestedRow .MuiDataGrid-cell--pinnedRight':
            {
              backgroundColor: '#edf0f4 !important',
            },
          '& .disabledSelectedOptionCell': {
            borderBottom: '1px solid red !important',
          },
          '& .evenRow': { backgroundColor: '#f7f9fa' },
          '& .oddRow': { backgroundColor: '#fff' },

          [`& .${gridClasses.cell}`]: {
            padding: '0px 10px',
            marginLeft: 0,
            marginRight: 0,
          },

          [`& .${gridClasses.columnHeader}`]: {
            padding: '0px 10px',
            marginLeft: 0,
            marginRight: 0,
          },

          '& .MuiDataGrid-columnHeaderTitle': {
            padding: 0,
          },
          '& .MuiDataGrid-cellContent': {
            padding: 0,
          },

          '& .MuiDataGrid-withBorderColor': { border: '0px !important' },
          '& .MuiDataGrid-headerFilterRow': { borderBottom: '1px solid #000' },

          '@media (max-width: 768px)': {
            '& .MuiDataGrid-virtualScrollerContent': {
              marginRight: '110px',
            },
          },
        }}
      />
    )
  );
}
const useStyles = createUseStyles({
  gridContainer: {
    overflowY: 'hidden',
    flexGrow: 1,
    paddingBottom: '20px',
  },

  '@media (max-width: 600px)': {
    gridContainer: {
      height: '70dvh',
    },
  },

  gridBox: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflowY: 'auto',
  },

  paneContent: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    height: '100%',
    minHeight: 0,
    border: '1px solid lightgrey',
  },

  actionRow: {
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    paddingLeft: 5,
    gap: 5,
  },
  errorSlot: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'width 0.15s ease',
  },

  undoSlot: {
    display: 'flex',
    alignItems: 'center',
  },
  rightPanelContentResponsive: {
    padding: '0.75rem 0.5rem !important',
  },
});
